import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button, Card, Empty, Form, Input, List, Skeleton, message, Modal, Popconfirm, Space } from "antd";
import { PlusCircle, RefreshCw, FolderOpen, Edit3, Trash2 } from "lucide-react";
import communityAPI from "../../../api/community";
import { useCommunityAuth } from "../../../contexts/CommunityAuthContext";

const normalizeList = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (payload && Array.isArray(payload.results)) {
    return payload.results;
  }
  return [];
};

const CategoryManager = ({ className = "", showHeader = true }) => {
  const { isAdmin } = useCommunityAuth();
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [rowAction, setRowAction] = useState(null);
  const [filterText, setFilterText] = useState("");

  const fetchCategories = useCallback(async () => {
    if (!isAdmin) {
      return;
    }
    setLoading(true);
    try {
      const response = await communityAPI.categories.getWithCounts();
      setCategories(normalizeList(response.data));
    } catch (err) {
      console.error("Failed to load categories", err);
      message.error("Unable to load categories right now.");
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (editingCategory) {
      editForm.setFieldsValue({
        name: editingCategory.name,
        description: editingCategory.description || "",
        icon: editingCategory.icon || "",
        slug: editingCategory.slug,
      });
    } else {
      editForm.resetFields();
    }
  }, [editingCategory, editForm]);

  const filteredCategories = useMemo(() => {
    const query = filterText.trim().toLowerCase();
    if (!query) {
      return categories;
    }
    return categories.filter((item) => {
      const nameMatch = item.name?.toLowerCase().includes(query);
      const descMatch = (item.description || "").toLowerCase().includes(query);
      const slugMatch = item.slug?.toLowerCase().includes(query);
      return nameMatch || descMatch || slugMatch;
    });
  }, [categories, filterText]);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    const payload = {
      name: values.name.trim(),
      description: (values.description || "").trim(),
      icon: (values.icon || "").trim(),
    };
    try {
      const response = await communityAPI.categories.create(payload);
      const created = response.data;
      message.success(`Category "${created.name}" created.`);
      form.resetFields();
      // Ensure new category appears immediately without waiting for refetch
      setCategories((prev) => [
        { ...created, post_count: 0 },
        ...prev.filter((item) => item.id !== created.id),
      ]);
    } catch (err) {
      console.error("Failed to create category", err);
      const detail =
        err.response?.data?.detail ||
        err.response?.data?.name ||
        err.response?.data?.slug ||
        "Failed to create category.";
      if (Array.isArray(detail)) {
        message.error(detail.join(" "));
      } else {
        message.error(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
  };

  const handleModalCancel = () => {
    setEditingCategory(null);
  };

  const handleUpdate = async (values) => {
    if (!editingCategory) {
      return;
    }
    setModalLoading(true);
    const payload = {
      name: values.name.trim(),
      description: (values.description || "").trim(),
      icon: (values.icon || "").trim(),
    };
    if (values.slug !== undefined) {
      const trimmedSlug = (values.slug || "").trim();
      if (!trimmedSlug && editingCategory.slug) {
        payload.slug = "";
      } else if (trimmedSlug && trimmedSlug !== editingCategory.slug) {
        payload.slug = trimmedSlug;
      }
    }

    try {
      const response = await communityAPI.categories.update(
        editingCategory.slug,
        payload
      );
      const updated = response.data;
      message.success(`Category "${updated.name}" updated.`);
      await fetchCategories();
      setEditingCategory(null);
    } catch (err) {
      console.error("Failed to update category", err);
      const detail =
        err.response?.data?.detail ||
        err.response?.data?.name ||
        err.response?.data?.slug ||
        "Failed to update category.";
      if (Array.isArray(detail)) {
        message.error(detail.join(" "));
      } else {
        message.error(detail);
      }
    } finally {
      setModalLoading(false);
    }
  };

  const handleDelete = async (category) => {
    setRowAction(category.slug);
    try {
      await communityAPI.categories.delete(category.slug);
      message.success(`Category "${category.name}" removed.`);
      setCategories((prev) => prev.filter((item) => item.id !== category.id));
    } catch (err) {
      console.error("Failed to delete category", err);
      const detail = err.response?.data?.detail || "Failed to delete category.";
      if (Array.isArray(detail)) {
        message.error(detail.join(" "));
      } else {
        message.error(detail);
      }
    } finally {
      setRowAction(null);
    }
  };

  if (!isAdmin) {
    return null;
  }

  const renderHeader = () => (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
        <FolderOpen className="h-5 w-5" />
        Manage Categories
      </div>
      <Button
        size="small"
        type="text"
        icon={<RefreshCw className="h-4 w-4" />}
        onClick={fetchCategories}
        loading={loading}
      >
        Refresh
      </Button>
    </div>
  );

  return (
    <Card
      className={`relative overflow-hidden rounded-3xl border border-gray-200/70 dark:border-gray-700/70 bg-white/80 dark:bg-gray-900/80 backdrop-blur shadow-lg [&.ant-card]:dark:bg-gray-900 [&_.ant-card-body]:dark:bg-gray-900 [&_.ant-card-head]:dark:bg-gray-900 [&_.ant-card-head]:dark:border-gray-700 [&_.ant-card-head-title]:dark:text-white ${className}`.trim()}
      title={showHeader ? renderHeader() : null}
    >
      {!showHeader && <div className="mb-4">{renderHeader()}</div>}
      <div className="grid gap-6 md:grid-cols-2">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
          disabled={submitting}
          className="rounded-2xl border border-dashed border-indigo-200 dark:border-indigo-600/50 bg-indigo-50/60 dark:bg-indigo-950/30 p-5 [&_.ant-input]:dark:bg-gray-800 [&_.ant-input]:dark:border-gray-700 [&_.ant-input]:dark:text-white [&_.ant-input]:dark:placeholder-gray-400 [&_.ant-form-item-extra]:dark:text-gray-500 [&_.ant-input-affix-wrapper]:dark:bg-gray-800 [&_.ant-input-affix-wrapper]:dark:border-gray-700"
        >
          <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-indigo-500 dark:text-indigo-300 mb-4">
            <PlusCircle className="h-4 w-4" />
            Create New Category
          </div>
          <Form.Item
            name="name"
            label={<span className="font-semibold dark:text-gray-200">Name</span>}
            rules={[{ required: true, message: "Provide a category name." }]}
          >
            <Input placeholder="e.g. Incident Response" maxLength={100} allowClear />
          </Form.Item>
          <Form.Item
            name="description"
            label={<span className="font-semibold dark:text-gray-200">Description</span>}
            extra="Optional. Briefly describe when to use this category."
          >
            <Input.TextArea rows={3} maxLength={300} allowClear showCount />
          </Form.Item>
          <Form.Item
            name="icon"
            label={<span className="font-semibold dark:text-gray-200">Icon</span>}
            extra="Optional. Provide an icon key used by the UI."
          >
            <Input placeholder="e.g. shield" maxLength={50} allowClear />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={submitting}
            className="mt-2 inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 font-semibold"
          >
            <PlusCircle className="h-4 w-4" />
            Add Category
          </Button>
        </Form>

        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-950/70 p-5">
          <Space direction="vertical" size="large" className="w-full">
            <Input.Search
              allowClear
              placeholder="Filter by name or slug"
              value={filterText}
              onChange={(event) => setFilterText(event.target.value)}
              onSearch={(value) => setFilterText(value)}
              className="rounded-lg [&_.ant-input]:dark:bg-gray-800 [&_.ant-input]:dark:border-gray-700 [&_.ant-input]:dark:text-white [&_.ant-input]:dark:placeholder-gray-400 [&_.ant-input-group-addon]:dark:bg-gray-700 [&_.ant-input-group-addon]:dark:border-gray-700 [&_.ant-btn]:dark:bg-primary [&_.ant-btn]:dark:border-primary [&_.ant-btn]:dark:text-white [&_.ant-input-affix-wrapper]:dark:bg-gray-800 [&_.ant-input-affix-wrapper]:dark:border-gray-700 [&_.ant-input-affix-wrapper]:dark:text-white [&_.ant-input-clear-icon]:dark:text-gray-400"
            />
            {loading && categories.length === 0 ? (
              <div className="space-y-4">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={`category-skeleton-${index}`} active paragraph={{ rows: 1 }} />
                ))}
              </div>
            ) : filteredCategories.length === 0 ? (
              <Empty description={filterText ? "No categories match your search." : "No categories yet. Create the first one."} />
            ) : (
              <div className="max-h-80 overflow-y-auto pr-1">
                <List
                  itemLayout="vertical"
                  dataSource={filteredCategories}
                  loading={loading}
                  renderItem={(item) => (
                    <List.Item
                      key={item.id}
                      className="px-0"
                      actions={[
                        <Button
                          key="edit"
                          size="small"
                          type="text"
                          icon={<Edit3 className="h-4 w-4" />}
                          onClick={() => handleEdit(item)}
                        >
                          Edit
                        </Button>,
                        <Popconfirm
                          key="delete"
                          title="Delete category"
                          description="This will remove the category from new posts, but existing posts keep their link."
                          okText="Delete"
                          okType="danger"
                          onConfirm={() => handleDelete(item)}
                        >
                          <Button
                            size="small"
                            danger
                            type="text"
                            icon={<Trash2 className="h-4 w-4" />}
                            loading={rowAction === item.slug}
                          >
                            Delete
                          </Button>
                        </Popconfirm>,
                      ]}
                    >
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-base font-semibold text-gray-900 dark:text-gray-100">
                            {item.name}
                          </span>
                          <span className="text-xs font-semibold uppercase tracking-wider text-indigo-500 dark:text-indigo-300">
                            {item.post_count ?? 0} posts
                          </span>
                        </div>
                        {item.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {item.description}
                          </p>
                        )}
                        <div className="text-xs text-gray-400 dark:text-gray-500">
                          Slug: {item.slug}
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              </div>
            )}
          </Space>
        </div>
      </div>

      <Modal
        title={`Edit Category${editingCategory ? `: ${editingCategory.name}` : ""}`}
        open={Boolean(editingCategory)}
        onCancel={handleModalCancel}
        onOk={() => editForm.submit()}
        confirmLoading={modalLoading}
        destroyOnHidden
        okText="Save changes"
        className="[&_.ant-modal-content]:dark:bg-gray-900 [&_.ant-modal-header]:dark:bg-gray-900 [&_.ant-modal-header]:dark:border-gray-700 [&_.ant-modal-title]:dark:text-white [&_.ant-modal-close]:dark:text-gray-400 [&_.ant-modal-close:hover]:dark:text-white [&_.ant-modal-footer]:dark:bg-gray-900 [&_.ant-modal-footer]:dark:border-gray-700"
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdate}
          requiredMark={false}
          className="[&_.ant-form-item-label>label]:dark:text-gray-200 [&_.ant-input]:dark:bg-gray-800 [&_.ant-input]:dark:border-gray-700 [&_.ant-input]:dark:text-white [&_.ant-input]:dark:placeholder-gray-400 [&_.ant-select-selector]:dark:bg-gray-800 [&_.ant-select-selector]:dark:border-gray-700 [&_.ant-select-selection-item]:dark:text-white [&_.ant-form-item-extra]:dark:text-gray-500 [&_.ant-input-affix-wrapper]:dark:bg-gray-800 [&_.ant-input-affix-wrapper]:dark:border-gray-700"
        >
          <Form.Item
            name="name"
            label={<span className="font-semibold dark:text-gray-200">Name</span>}
            rules={[{ required: true, message: "Provide a category name." }]}
          >
            <Input maxLength={100} allowClear placeholder="Category name" />
          </Form.Item>
          <Form.Item
            name="description"
            label={<span className="font-semibold dark:text-gray-200">Description</span>}
            extra="Optional. Keep it short and actionable."
          >
            <Input.TextArea rows={3} maxLength={300} allowClear showCount placeholder="Brief description" />
          </Form.Item>
          <Form.Item
            name="icon"
            label={<span className="font-semibold dark:text-gray-200">Icon</span>}
            extra="Optional icon key used in the UI."
          >
            <Input maxLength={50} allowClear placeholder="Icon name (optional)" />
          </Form.Item>
          <Form.Item
            name="slug"
            label={<span className="font-semibold dark:text-gray-200">Slug</span>}
            extra="Optional. Leave blank to auto-generate from the name."
          >
            <Input maxLength={150} allowClear placeholder="custom-slug" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

CategoryManager.propTypes = {
  className: PropTypes.string,
  showHeader: PropTypes.bool,
};

export default CategoryManager;
